/* eslint-disable no-console */
import { useCallback, useEffect, useRef } from "react";

import { resolveWsUpdate } from "../mapper/lads-ws-update";
import { useChannelsStore } from "../store/channels-store";
import { useDevicesStore } from "../store/devices-store";
import { useUnitStore } from "../store/unit-store";
import {
  type DeviceModelChange,
  type DeviceSemanticData,
  ladsWebSocketService,
  type UAFunctionValueMessage,
} from "../util/websocket-lads";
import type { DeviceChannels } from "../view-model";

/**
 * Hook for subscribing to live value updates for a specific device/unit
 */
export type UseLADSValuesConfig = {
  roomId: string;
  unitId?: string;
  /** Parent device browseName (e.g., "SO1FW400858") for filtering */
  parentDeviceId: string;
  channels?: DeviceChannels;
  enabled?: boolean;
};

export function useLADSValues({
  channels,
  enabled = true,
  parentDeviceId,
  roomId,
  unitId,
}: UseLADSValuesConfig) {
  const { updateDeviceChannels } = useDevicesStore();
  const { setChannelValues } = useChannelsStore();
  const channelsRef = useRef<DeviceChannels>(channels || {});
  const unitIdRef = useRef(unitId);
  const parentDeviceIdRef = useRef(parentDeviceId);

  useEffect(() => {
    channelsRef.current = channels || {};
  }, [channels]);

  useEffect(() => {
    unitIdRef.current = unitId;
  }, [unitId]);

  useEffect(() => {
    parentDeviceIdRef.current = parentDeviceId;
  }, [parentDeviceId]);

  // Room subscription
  useEffect(() => {
    if (!enabled || !roomId) return;
    ladsWebSocketService.subscribe(roomId);
    return () => {
      ladsWebSocketService.unsubscribe(roomId);
    };
  }, [roomId, enabled]);

  // Value handler
  useEffect(() => {
    if (!enabled) return;

    const unsubscribe = ladsWebSocketService.onValue(
      (message: UAFunctionValueMessage) => {
        const action = resolveWsUpdate(message, {
          parentDeviceId: parentDeviceIdRef.current,
          unitKey: unitIdRef.current,
          channels: channelsRef.current ?? {},
          getMetadata: (d, f) => ladsWebSocketService.getMetadata(d, f),
        });

        switch (action.type) {
          case "unit-state": {
            useUnitStore.getState().updateUnit(action.unitKey, {
              state: action.state,
            });
            break;
          }
          case "unit-program": {
            const { getUnit, updateUnit } = useUnitStore.getState();
            const existing = getUnit(action.unitKey)?.activeProgram ?? {};
            updateUnit(action.unitKey, {
              activeProgram: { ...existing, ...action.program },
            });
            break;
          }
          case "channels": {
            channelsRef.current = action.channels;
            // Channels store stays keyed `${parentDeviceId}/${unitId}` — both
            // writer and reader (use-channels) use that form consistently.
            const currentUnit = unitIdRef.current;
            if (currentUnit) {
              const scopedUnitId = `${parentDeviceIdRef.current}/${currentUnit}`;
              updateDeviceChannels(scopedUnitId, action.channels);
              setChannelValues(scopedUnitId, action.channels);
            }
            break;
          }
          // case "ignore": no-op
        }
      }
    );

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    isConnected: ladsWebSocketService.isSocketConnected(),
  };
}

/**
 * Callbacks for device change events
 */
export type DeviceChangedCallbacks = {
  onDeviceAdded?: (deviceId: string) => void;
  onDeviceRemoved?: (deviceId: string) => void;
  onDeviceStatusChange?: (deviceId: string, isOnline: boolean) => void;
  onDeviceMetadataChange?: (deviceId: string, data: DeviceSemanticData) => void;
};

// ---------------------------------------------------------------------------
// Global device-store reconcilers — one explicit handler per OPC-UA event.
// Registered once; the per-component callbacks (useLADSDeviceChanged) run in
// addition for extra actions like refetch.
// ---------------------------------------------------------------------------
function applyDeviceState(deviceId: string, isOnline: boolean) {
  console.log(`[WS] UADeviceState ${deviceId} → online=${isOnline}`);
  useDevicesStore.getState().updateDevice(deviceId, {
    state: { status: isOnline ? "online" : "offline" },
    stateChannels: {
      status: {
        id: "status",
        name: "Status",
        status: "on",
        current: {
          value: isOnline ? "online" : "offline",
          timestamp: new Date(),
        },
      },
    },
  });
}

function applyModelChange(deviceId: string, change: DeviceModelChange) {
  console.log(`[WS] UAGeneralModelChange (${change}) for ${deviceId}`);
  if (change === "removed") {
    useDevicesStore.getState().removeDevice(deviceId);
  }
  // "added" needs a full fetch — driven by the per-component onDeviceAdded callback.
}

function applySemanticChange(deviceId: string, data: DeviceSemanticData) {
  console.log(`[WS] UASemanticChange for ${deviceId}:`, data);
  const updates: Partial<{ name: string; location: { room: string } }> = {};
  if (data.componentName) {
    const atIndex = deviceId.lastIndexOf("@");
    const suffix = atIndex > 0 ? deviceId.slice(Math.max(0, atIndex)) : "";
    updates.name = `${data.componentName}${suffix}`;
  }
  if (data.hierarchicalLocation) {
    updates.location = { room: data.hierarchicalLocation };
  }
  if (Object.keys(updates).length > 0) {
    useDevicesStore.getState().updateDevice(deviceId, updates);
  }
}

let deviceStoreReconcilerRegistered = false;
/** Register the global device-store reconcilers exactly once. */
function ensureDeviceStoreReconciler() {
  if (deviceStoreReconcilerRegistered) return;
  deviceStoreReconcilerRegistered = true;
  ladsWebSocketService.onDeviceState(applyDeviceState);
  ladsWebSocketService.onModelChange(applyModelChange);
  ladsWebSocketService.onSemanticChange(applySemanticChange);
}

/**
 * Hook for subscribing to device state changes (status, added, removed, metadata).
 *
 * Fans out the per-component `callbacks` only -- the device store is reconciled
 * centrally by reconcileDeviceStore (registered once), so mounting this hook in
 * several places no longer multiplies the store writes.
 */
export function useLADSDeviceChanged(
  roomId: string,
  callbacks: DeviceChangedCallbacks = {},
  enabled = true
) {
  const callbacksRef = useRef(callbacks);

  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  // Keep the single global store reconciler alive while any consumer is mounted.
  useEffect(() => {
    ensureDeviceStoreReconciler();
  }, []);

  useEffect(() => {
    if (!enabled || !roomId) return;
    ladsWebSocketService.subscribe(roomId);
    return () => {
      ladsWebSocketService.unsubscribe(roomId);
    };
  }, [roomId, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const unsubscribers = [
      ladsWebSocketService.onDeviceState((deviceId, isOnline) =>
        callbacksRef.current.onDeviceStatusChange?.(deviceId, isOnline)
      ),
      ladsWebSocketService.onModelChange((deviceId, change) => {
        if (change === "added") callbacksRef.current.onDeviceAdded?.(deviceId);
        else callbacksRef.current.onDeviceRemoved?.(deviceId);
      }),
      ladsWebSocketService.onSemanticChange((deviceId, data) =>
        callbacksRef.current.onDeviceMetadataChange?.(deviceId, data)
      ),
    ];

    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}

/**
 * Hook for managing multiple room subscriptions
 */
export function useLADSRoomManager() {
  const subscriptions = useRef<Map<string, string>>(new Map());

  const subscribe = useCallback((key: string, roomId: string) => {
    subscriptions.current.set(key, roomId);
    ladsWebSocketService.subscribe(roomId);
  }, []);

  const unsubscribe = useCallback((key: string) => {
    const roomId = subscriptions.current.get(key);
    if (roomId) {
      ladsWebSocketService.unsubscribe(roomId);
      subscriptions.current.delete(key);
    }
  }, []);

  const unsubscribeAll = useCallback(() => {
    for (const [key] of subscriptions.current) {
      unsubscribe(key);
    }
  }, [unsubscribe]);

  useEffect(() => () => unsubscribeAll(), [unsubscribeAll]);

  return {
    subscribe,
    unsubscribe,
    unsubscribeAll,
    isConnected: ladsWebSocketService.isSocketConnected(),
  };
}
